import { UserRole } from '#enums';
import { UserDetails } from '#models';
import { inject, Injectable } from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { AccountInfo, RedirectRequest } from '@azure/msal-browser';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _msalService = inject(MsalService);
  private _msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);

  private _userDetails$ = new BehaviorSubject<UserDetails | null>(null);
  readonly userDetails$ = this._userDetails$.asObservable();

  setUserDetails(userDetails: UserDetails | null) {
    this._userDetails$.next(userDetails);
  }

  getUserDetails() {
    return this._userDetails$.getValue();
  }

  isAuthorized(roles: UserRole[] = []): boolean {
    const userDetails = this._userDetails$.getValue();
    if (!userDetails) {
      return false;
    }

    if (roles.includes(UserRole.User)) {
      return userDetails.hasAccess;
    }

    // Admins always have access regardless of which specific roles are listed
    if (userDetails.isAdmin) {
      return true;
    }

    return roles.includes(userDetails.role as UserRole);
  }

  isAuthenticated() {
    const accounts = this._msalService.instance.getAllAccounts();
    return accounts && accounts.length > 0;
  }

  getAccount() {
    const accounts = this._msalService.instance.getAllAccounts();
    return accounts && accounts.length > 0 ? accounts[0] : null;
  }

  getRoles() {
    const account = this.getAccount();
    if (!account) {
      return [];
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const idTokenClaims = account.idTokenClaims as any;
    return idTokenClaims.roles || [];
  }

  isAdmin(): boolean {
    const roles = this.getRoles();
    return roles.includes(UserRole.Admin);
  }

  getAccountEmail() {
    return this.getAccount()?.username || '';
  }

  initialize(): Promise<void> {
    return this._msalService.instance.initialize().then(async () => {
      // Handle redirect from login page
      const result = await this._msalService.instance.handleRedirectPromise();

      if (result !== null) {
        // User just completed login redirect
        this._msalService.instance.setActiveAccount(result.account);
        this._populateUserDetails(result.account);
        return;
      }

      // Check if user already has an active session
      if (this.isAuthenticated()) {
        const account = this.getAccount();
        this._msalService.instance.setActiveAccount(account);

        // Force a silent token refresh to pick up any role changes since last login
        try {
          const refreshed = await this._msalService.instance.acquireTokenSilent(
            {
              ...(this._msalGuardConfig.authRequest as RedirectRequest),
              account: account!,
            },
          );
          this._populateUserDetails(refreshed.account);
        } catch {
          // Silent refresh failed (e.g. network issue) — fall back to cached claims
          this._populateUserDetails(account);
        }
        return;
      }

      return firstValueFrom(
        this._msalService.loginRedirect(
          this._msalGuardConfig.authRequest as RedirectRequest,
        ),
      );
    });
  }

  private _populateUserDetails(account: AccountInfo | null) {
    if (!account) return;
    const idTokenClaims = account.idTokenClaims as Record<string, unknown>;

    const roles = (idTokenClaims?.['roles'] as string[]) ?? [];
    // Fall back to email when no app role is assigned yet
    const role = roles[0] ?? account.username;

    this.setUserDetails({
      email: account.username,
      username: account.name ?? account.username,
      role,
      isAdmin: roles.includes(UserRole.Admin),
      // Users with multiple roles assigned are treated as unauthorized (misconfigured)
      hasAccess: roles.length === 1,
    });
  }
}
